import { useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

type Options<TState extends { search: string }, TSearch> = {
  search: TSearch;
  parseSearch: (rawSearch: unknown) => TSearch;
  fromSearch: (search: TSearch) => TState;
  writeSearch: (state: TState, options: { replace: boolean }) => void;
};

export function useSearchBackedState<
  TState extends { search: string },
  TSearch,
>({
  search,
  parseSearch,
  fromSearch,
  writeSearch,
}: Options<TState, TSearch>): [TState, (next: TState) => void] {
  const router = useRouter();
  const [state, setState] = useState(() => fromSearch(search));
  const writing = useRef(false);

  useEffect(
    () =>
      router.history.subscribe(({ location }) => {
        const isOwnScreen =
          location.pathname === router.state.resolvedLocation?.pathname;

        if (writing.current || !isOwnScreen) {
          return;
        }

        setState(
          fromSearch(parseSearch(router.options.parseSearch(location.search))),
        );
      }),
    [router, parseSearch, fromSearch],
  );

  function updateState(next: TState) {
    setState(next);

    writing.current = true;
    writeSearch(next, { replace: next.search !== state.search });
    writing.current = false;
  }

  return [state, updateState];
}
